import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { OptimizedRouteWrapper as RouteWrapper } from '@/components/common/OptimizedRouteWrapper';
import { ManagerRouteWrapper } from '@/components/common/ManagerRouteWrapper';
import { ROUTE_COMPONENTS } from '@/hooks/useRouteConfig';

// Lazy load auth/entry pages for faster initial bundle
const AuthPage = lazy(() => import('@/pages/AuthPage'));
const PasswordResetPage = lazy(() => import('@/pages/PasswordResetPage'));
const PendingApprovalPage = lazy(() => import('@/pages/PendingApprovalPage').then(m => ({ default: m.PendingApprovalPage })));
const NotFound = lazy(() => import('@/pages/NotFound'));
const LandingPage = lazy(() => import('@/pages/LandingPage'));

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

export function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Home route - Public landing page for non-authenticated users */}
      <Route 
        path="/" 
        element={
          user ? (
            <Navigate to="/tracker/log" replace />
          ) : (
            <Suspense fallback={<PageLoader />}>
              <LandingPage />
            </Suspense>
          )
        } 
      />

      {/* Auth routes */}
      <Route path="/auth" element={<Suspense fallback={<PageLoader />}><AuthPage /></Suspense>} />
      <Route path="/login" element={<Suspense fallback={<PageLoader />}><AuthPage /></Suspense>} />
      <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><PasswordResetPage /></Suspense>} />
      <Route path="/pending-approval" element={<Suspense fallback={<PageLoader />}><PendingApprovalPage /></Suspense>} />

      {/* Tracker routes */}
      <Route path="/disc" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.DISCTestPage /></RouteWrapper>} />

      {/* Tracker routes */}
      <Route path="/tracker/log" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.LogActivityPage /></RouteWrapper>} />
      <Route path="/tracker/feed" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ActivityFeedPage /></RouteWrapper>} />
      <Route path="/tracker/pet" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.PetPage /></RouteWrapper>} />
      <Route path="/tracker/redeem" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.RedemptionPage /></RouteWrapper>} />
      <Route path="/tracker/wheel" element={<ManagerRouteWrapper><ROUTE_COMPONENTS.WheelOfFortunePage /></ManagerRouteWrapper>} />
      <Route path="/tracker/reflections" element={<Navigate to="/tracker/reflections/my-reflection" replace />} />
      <Route path="/tracker/reflections/my-reflection" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.WeeklyReflectionsPage /></RouteWrapper>} />
      <Route path="/tracker/reflections/community" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.WeeklyReflectionsPage /></RouteWrapper>} />
      <Route path="/tracker/reflections/tracking" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.WeeklyReflectionsPage /></RouteWrapper>} />
      <Route path="/tracker/reflections/settings" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.WeeklyReflectionsPage /></RouteWrapper>} />
      <Route path="/tracker/club" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ClubPage /></RouteWrapper>} />
      <Route path="/tracker/achievements" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.AchievementsPage /></RouteWrapper>} />
      <Route path="/tracker/sales-activity-calc" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.PledgeCalculatorPage /></RouteWrapper>} />
      <Route path="/tracker/activity-calculator" element={<Navigate to="/tracker/activity-calculator/forward" replace />} />
      <Route path="/tracker/activity-calculator/forward" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ActivityCalculator /></RouteWrapper>} />
      <Route path="/tracker/activity-calculator/reverse" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ActivityCalculator /></RouteWrapper>} />
      <Route path="/habits" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.HabitsPage /></RouteWrapper>} />
      <Route path="/tracker/commission-calculator" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.CommissionCalculatorPage /></RouteWrapper>} />
      <Route path="/tracker/user-breakdown/:userSlug" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.UserActivityBreakdownPage /></RouteWrapper>} />
      <Route path="/tracker/win-credits" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.WinCreditsPage /></RouteWrapper>} />
      
      {/* Tracker Admin routes */}
      <Route path="/tracker/admin/users" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TrackerAdminPage /></RouteWrapper>} />
      <Route path="/tracker/admin/rewards" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TrackerAdminPage /></RouteWrapper>} />
      <Route path="/tracker/admin/wheel" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TrackerAdminPage /></RouteWrapper>} />
      <Route path="/tracker/admin/settings" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TrackerAdminPage /></RouteWrapper>} />
      <Route path="/tracker/admin/w-users/:userId" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.UserWDollarDetailPage /></RouteWrapper>} />
      <Route path="/tracker/admin/w-users" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.WDollarUsersPage /></RouteWrapper>} />
      <Route path="/tracker/admin/w-ops" element={<Navigate to="/tracker/admin/w-ops/overview" replace />} />
      <Route path="/tracker/admin/w-ops/overview" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.WDollarOverviewPage /></RouteWrapper>} />
      <Route path="/tracker/admin/w-ops/history" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.WDollarCreditHistoryPage /></RouteWrapper>} />
      <Route path="/tracker/admin" element={<Navigate to="/tracker/admin/users" replace />} />
      
      <Route path="/tracker/stats" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.StatsPage /></RouteWrapper>} />

      {/* Admin and management routes */}
      <Route path="/admin" element={<Navigate to="/tracker/admin" replace />} />
      <Route path="/feedback" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.Feedback /></RouteWrapper>} />
      <Route path="/income-calculator" element={<Navigate to="/income-calculator/income-layers/detailed" replace />} />
      <Route path="/income-calculator/*" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.InsuranceCalculatorPage /></RouteWrapper>} />
      <Route path="/coaching" element={<Navigate to="/coaching/settings" replace />} />
      <Route path="/coaching/settings" element={<ManagerRouteWrapper><ROUTE_COMPONENTS.CoachingSettings /></ManagerRouteWrapper>} />
      <Route path="/coaching/access" element={<ManagerRouteWrapper><ROUTE_COMPONENTS.CoachingSettings /></ManagerRouteWrapper>} />
      <Route path="/coaching/schedule" element={<ManagerRouteWrapper><ROUTE_COMPONENTS.CoachingSettings /></ManagerRouteWrapper>} />
      <Route path="/coaching/bookings" element={<ManagerRouteWrapper><ROUTE_COMPONENTS.CoachingSettings /></ManagerRouteWrapper>} />

      {/* Resource routes */}
      <Route path="/sales-resources" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.SalesResourcesPage /></RouteWrapper>} />
      <Route path="/manager-tools" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ManagerToolsPage /></RouteWrapper>} />
      <Route path="/consultant-tools" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ConsultantToolsPage /></RouteWrapper>} />
      <Route path="/aia-links" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.AiaLinksPage /></RouteWrapper>} />
      <Route path="/resources-hub" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ResourcesHubPage /></RouteWrapper>} />
      
      {/* Calculator routes */}
      <Route path="/manager-calculator" element={<ManagerRouteWrapper><ROUTE_COMPONENTS.ManagerCalculator /></ManagerRouteWrapper>} />
      
      {/* Manager Dashboard */}
      <Route path="/tracker/manager-dashboard" element={<ManagerRouteWrapper><ROUTE_COMPONENTS.ManagerDashboardPage /></ManagerRouteWrapper>} />

      {/* Coaching and booking routes */}
      <Route path="/coaching/:slug" element={<RouteWrapper><ROUTE_COMPONENTS.CoachingPage /></RouteWrapper>} />
      <Route path="/booking-confirmation/:bookingId" element={<RouteWrapper><ROUTE_COMPONENTS.BookingConfirmation /></RouteWrapper>} />

      {/* Legal pages (public for Google OAuth verification) */}
      <Route path="/privacy-policy" element={<RouteWrapper><ROUTE_COMPONENTS.PrivacyPolicyPage /></RouteWrapper>} />
      <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
      <Route path="/terms" element={<RouteWrapper><ROUTE_COMPONENTS.TermsOfServicePage /></RouteWrapper>} />

      {/* Detail pages */}
      <Route path="/activity/:id" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.ActivityDetailPage /></RouteWrapper>} />
      <Route path="/task/:type/:id" element={<RouteWrapper requiresAuth><ROUTE_COMPONENTS.TaskDetailPage /></RouteWrapper>} />

      {/* Legacy redirects */}
      <Route path="/dashboard" element={<Navigate to="/tracker/log" replace />} />
      <Route path="/dashboard/*" element={<Navigate to="/tracker/log" replace />} />

      {/* Fallback */}
      <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
    </Routes>
  );
}
